import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import theme from "../theme";
import { getRecentNews } from "../Api/ForNews";

const AutoCarousel = () => {
  const scrollRef = useRef(null);
  const [showThumb, setShowThumb] = useState(false);
  const [thumbStyle, setThumbStyle] = useState({ width: 0, left: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragData = useRef({ startX: 0, scrollLeft: 0 });
  const [newsItems, setNewsItems] = useState([]);
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    getRecentNews().then((res) => {
      const sortedItems = (res.items || []).sort((a, b) => {
        const dateA = new Date(a.fields.date);
        const dateB = new Date(b.fields.date);
        return dateB - dateA; // descending: latest first
      });

      setNewsItems(sortedItems);
      setAssets(res.includes?.Asset || []);
    });
  }, []);

  // Update thumb position & width
  const updateThumb = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;

    const thumbWidth = (clientWidth / scrollWidth) * clientWidth;
    const thumbLeft = (scrollLeft / scrollWidth) * clientWidth;

    setThumbStyle({ width: thumbWidth, left: thumbLeft });
  };

  // Show/hide thumb on scroll
  const handleScroll = () => {
    updateThumb();
    setShowThumb(true);
    clearTimeout(handleScroll._timeout);
    handleScroll._timeout = setTimeout(() => setShowThumb(false), 1000);
  };

  // Arrow scrolling
  const scrollByAmount = (amount) => {
    scrollRef.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  // Resize observer
  useEffect(() => {
    updateThumb();
    const ro = new ResizeObserver(updateThumb);
    if (scrollRef.current) ro.observe(scrollRef.current);
    return () => ro.disconnect();
  }, []);

  // Drag events
  const startDrag = (e) => {
    setIsDragging(true);
    dragData.current = {
      startX: e.pageX || e.touches?.[0]?.pageX,
      scrollLeft: scrollRef.current.scrollLeft,
    };
  };

  const duringDrag = (e) => {
    if (!isDragging) return;
    const x = e.pageX || e.touches?.[0]?.pageX;
    const walk = (x - dragData.current.startX) * 1.5; // drag speed
    scrollRef.current.scrollLeft = dragData.current.scrollLeft - walk;
  };

  const stopDrag = () => {
    setIsDragging(false);
  };

  return (
    <Box
      sx={{
        py: { md: 15, sm: 5, xs: 2 },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        color: "#1D4979",
        userSelect: isDragging ? "none" : "auto", // prevent text selection
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{ mb: 4, color: "#0a2342", fontFamily: theme.fonts.secondary }}
      >
        RECENT NEWS
      </Typography>

      <Box sx={{ position: "relative", width: "100%", maxWidth: "1500px" }}>
        {/* Arrows */}
        <IconButton
          onClick={() => scrollByAmount(-300)}
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            zIndex: 2,
            transform: "translateY(-50%)",
            backgroundColor: "#fff",
            boxShadow: 1,
            "&:hover": { backgroundColor: "#f0f0f0" },
          }}
        >
          <ChevronLeft />
        </IconButton>

        <IconButton
          onClick={() => scrollByAmount(300)}
          sx={{
            position: "absolute",
            top: "50%",
            right: 0,
            zIndex: 2,
            transform: "translateY(-50%)",
            backgroundColor: "#fff",
            boxShadow: 1,
            "&:hover": { backgroundColor: "#f0f0f0" },
          }}
        >
          <ChevronRight />
        </IconButton>

        {/* Scroll container with drag & scroll */}
        <Box
          ref={scrollRef}
          onScroll={handleScroll}
          onMouseDown={startDrag}
          onMouseMove={duringDrag}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          onTouchStart={startDrag}
          onTouchMove={duringDrag}
          onTouchEnd={stopDrag}
          sx={{
            overflowX: "auto",
            display: "flex",
            gap: 2,
            px: { xs: 1, sm: 2, md: 4 },
            pb: 3,
            cursor: "default",
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {newsItems.map((item) => {
            const thumbId = item.fields.thumbnail?.sys.id;
            const asset = assets.find((a) => a.sys.id === thumbId);

            const imgUrl = asset?.fields?.file?.url
              ? `https:${asset.fields.file.url}`
              : null;

            return (
              <Box
                key={item.sys.id}
                sx={{
                  minWidth: "280px",
                  maxWidth: "300px",
                  flex: "0 0 auto",
                  py: 3,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  textAlign: "left",
                }}
              >
                {imgUrl && (
                  <Box
                    component="img"
                    src={imgUrl}
                    alt={item.fields.title}
                    sx={{
                      width: "100%",
                      height: "180px",
                      objectFit: "cover",
                      borderRadius: 2,
                      mb: 1.5,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5, color: "#306E9D", fontWeight: "bold" }}
                >
                  {new Date(item.fields.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  component="a"
                  href={item.fields.link}
                  target="_blank"
                  sx={{
                    display: "inline-block",
                    fontWeight: 500,
                    fontSize: "14px",
                    textDecoration: "none",
                    color: theme.palette.primary.main,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {item.fields.title}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Custom scrollbar thumb */}
        <Box
          sx={{
            position: "absolute",
            bottom: "6px",
            left: 0,
            height: "6px",
            borderRadius: "3px",
            backgroundColor: "#1D4979",
            width: `${thumbStyle.width}px`,
            transform: `translateX(${thumbStyle.left}px)`,
            transition: "opacity 0.5s ease, transform 0.2s",
            opacity: showThumb ? 1 : 0,
            pointerEvents: "none",
          }}
        />
      </Box>
    </Box>
  );
};

export default AutoCarousel;
